import { Button, Text, useKumoToastManager } from '@cloudflare/kumo'
import { CopyIcon, EyeIcon, EyeSlashIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../i18n'

const TOUCH = { width: 44, height: 44 } as const
const MASK = '•'.repeat(12)

interface CredentialRowProps {
  label: string
  value: string
  /** Mask the value until `revealed` is true; renders the show/hide toggle. */
  secret?: boolean
  revealed?: boolean
  onToggleReveal?: () => void
}

export function CredentialRow({
  label,
  value,
  secret = false,
  revealed = false,
  onToggleReveal,
}: CredentialRowProps) {
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const masked = secret && !revealed

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      toasts.add({ variant: 'success', title: t('wifi.copied', { label }) })
    } catch {
      toasts.add({ variant: 'error', title: t('wifi.copyError') })
    }
  }

  return (
    <div className="flex items-center justify-between gap-2" style={{ minHeight: 56 }}>
      <div className="flex min-w-0 flex-col">
        <Text variant="secondary">{label}</Text>
        <Text variant="mono" DANGEROUS_className="min-w-0" data-testid={`value-${label}`}>
          <span style={{ overflowWrap: 'anywhere' }}>{masked ? MASK : value}</span>
        </Text>
      </div>
      <div className="flex shrink-0 items-center">
        {secret && (
          <Button
            variant="ghost"
            shape="square"
            style={TOUCH}
            aria-label={revealed ? t('wifi.hidePassword') : t('wifi.showPassword')}
            aria-pressed={revealed}
            icon={revealed ? <EyeSlashIcon size={20} /> : <EyeIcon size={20} />}
            onClick={onToggleReveal}
          />
        )}
        <Button
          variant="ghost"
          shape="square"
          style={TOUCH}
          aria-label={t('wifi.copy', { label })}
          icon={<CopyIcon size={20} />}
          onClick={() => void copy()}
        />
      </div>
    </div>
  )
}
